import { i18n } from "@/plugins/vuetify";
import { ProjectDetails } from "@/data/models/ProjectDetails";
import { functions } from "@/main";

import { storage } from "../../main";
import { getClientId, getClientLanguage, getLangugageSettingsForReports, isIresClient, isSimulatingErp } from "@/data/repository/ClientRepository";
import { isActiveUserIresAdminAccount } from "@/data/repository/UserRepository";
import { createPdf } from "pdfmake/build/pdfmake";
import {
  Content,
  TableLayout,
  CustomTableLayout,
  PageSize,
  Margins,
  StyleDictionary,
} from "pdfmake/interfaces";
import { firestore } from "firebase/app";
import { saveReport } from "@/data/repository/ReportRepository";

import Report, {
  ReportUsage,
  ReportType,
  UploadStatus,
  ExtendedReportType,
  ExtendedReportUsage,
} from "@/data/models/Report";

import { filterOutUndefined, generateRandomUuid } from "@/utils/Utils";
import { formatDateCompact, formatDateForReportsFolderName } from "@/utils/DateFormatter";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getActiveUserId } from "@/data/repository/UserRepository";
import { ProgramChain } from "@/data/models/ProgramChain";
import { ReportSettingsModule } from "@/store/modules/ReportSettingsModule";
import { createHyDryCertificate } from "./PdfTemplateRepository";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface PdfReportData {
  content: Content;
  pageSize: PageSize;
  pageMargins: Margins;
  styles: StyleDictionary;
  images: { [key: string]: string };
}

function createDryingLegendTableLayout(): TableLayout {
  return {
    hLineWidth: function(i, node) {
      return i === 0 || i === node.table.body.length ? 1 : 0;
    },
    vLineWidth: function(i, node) {
      return i === 0 || i === node.table?.widths?.length ? 1 : 0;
    },
    hLineColor: "gray",
    vLineColor: "gray",
  };
}

function createDryingInfoTableLayout(): TableLayout {
  {
    return {
      paddingLeft(i) {
        return i === 0 ? -1 : 7;
      },
      paddingRight() {
        return 0;
      },
    };
  }
}

function createThinHeaderLineOnlyTableLayout(): TableLayout {
  const tableLayout: CustomTableLayout = {
    hLineWidth: (i) => (i == 1 ? 1 : 0),
    vLineWidth: () => 0,
    paddingLeft: (i) => (i === 0 ? 0 : 8),
    paddingRight: (i, node) =>
      i === (node?.table?.widths?.length ?? 0) - 1 ? 0 : 8,
  };

  return tableLayout;
}

function createFinalRowLineOnlyTableLayout(): TableLayout {
  const tableLayout: CustomTableLayout = {
    hLineWidth: (i) => (i == 0 ? 1 : 0),
    vLineWidth: () => 0,
    paddingLeft: (i) => (i === 0 ? 0 : 8),
    paddingRight: (i, node) =>
      i === (node?.table?.widths?.length ?? 0) - 1 ? 0 : 8,
  };

  return tableLayout;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setTableLayouts(data: any) {
  if (Array.isArray(data)) {
    for (const entry of data) {
      setTableLayouts(entry);
    }
  } else {
    const isObject = typeof data === "object";

    if (isObject) {
      const layoutId = data.layoutId;

      if (layoutId) {
        if (layoutId === "thinHeaderLineOnlyLayout") {
          data.layout = createThinHeaderLineOnlyTableLayout();
        } else if (layoutId === "dryingLegendLayout") {
          data.layout = createDryingLegendTableLayout();
        } else if (layoutId === "dryingInfoTableLayout") {
          data.layout = createDryingInfoTableLayout();
        } else if (layoutId === "thinFinalRowLineOnlyLayout") {
          data.layout = createFinalRowLineOnlyTableLayout();
        }
      }

      for (const key of Object.keys(data)) {
        setTableLayouts(data[key]);
      }
    }
  }
}

/**
 * The language a report is written in: the client's primary language when the company settings ask
 * for it, otherwise the language the user is looking at. Cloud renders the report body in this
 * locale, so the footer added here has to use it as well, or a German report gets an English footer.
 */
function getReportLocale() {
  return getLangugageSettingsForReports()
    ? getClientLanguage()
    : i18n.locale;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function getPdfReportData(data: any) {
  const createPdfReport = functions.httpsCallable("createPdfReportData");

  const locale = getReportLocale();

  const result = (await createPdfReport({
      ...data,
      locale,
      /*
      Whether IRES capabilities are switched on for this session, so the report can carry what the same person
      sees in the UI: intensity, unfiltered readings, run limit lines. With the app bar's long-press toggle on,
      an IRES user asks for -- and gets -- the client's report.

      Cloud does NOT take this as permission: it checks the signed-in user's own claim and the client's type as
      well (`ReportAudience.includeIresDataInReport`), so this can only narrow the report, never widen it.
      */
      includeIresData: isIresClient() || isActiveUserIresAdminAccount()
    }
  )).data;

  const pdfReportData = JSON.parse(result.data) as PdfReportData;

  /*
  Some table layouts need functions to work properly. 
  Those functions get lost when converting the report data to JSON. 
  We restore those layouts here.
  */
  setTableLayouts(pdfReportData);

  return pdfReportData;
}

async function getPdfReportDataDrying(
  serialNumber: string,
  chainIds: string[]
) {
  return getPdfReportData({
    serialNumber,
    chainIds,
  });
}

async function getPdfReportDataForProject(
  projectId: string,
  reportType: ReportType,
  reportUsage: ReportUsage
) {
  return getPdfReportData({
    reportType,
    reportUsage,
    projectId,
  });
}

function createPdfReportFromReportData(
  pdfReportData: PdfReportData,
  reportType: ReportType,
  vue: Vue,
  footerText: string | null
) {
  // The body comes from Cloud in the report locale, so the footer is written in it too.
  const reportLocale = getReportLocale();

  return createPdf({
    content: pdfReportData.content,
    pageSize: pdfReportData.pageSize,
    pageMargins: pdfReportData.pageMargins,
    styles: pdfReportData.styles,
    images: pdfReportData.images,
    footer: (currentPage, pageCount) => {
      /*
      The client can ask for the page number to carry the total ("7 von 13") instead of the
      page number alone. Unset means off, so existing reports keep their current footer.
      */
      const pageText = ReportSettingsModule.reportSettings.showTotalPageCount === true
        ? vue
            .$t("report.footer.formatPageOfPages", reportLocale, [currentPage, pageCount])
            .toString()
        : currentPage.toString();

      return [
        {
          height: 100,
          canvas: [
            {
              type: "line",
              x1: 30,
              y1: 0,
              x2: 595 - 30,
              y2: 0,
              lineWidth: 0.5,
              margin: [30, 10, 30, 0],
            },
          ],
        },
        {
          margin: [0, 10, 0, 0],
          columns: [
            {
              width: "*",
              alignment: "left",
              margin: [30, 0, 0, 0],
              text: vue
                .$t(
                  `report.footer.${
                    reportType === ReportType.DRYING
                      ? "dryingReport"
                      : reportType === ReportType.INTERIM_REPORT
                      ? "interimReport"
                      : "finalReport"
                  }`,
                  reportLocale
                )
                .toString(),
            },
            {
              width: "*",
              alignment: "center",
              height: 100,
              text: footerText ?? "",
            },
            {
              width: "*",
              alignment: "right",
              margin: [0, 0, 30, 0],
              text: pageText,
            },
          ],
        },
      ];
    },
    pageBreakBefore: (currentNode) => {
      if (currentNode.id?.startsWith("dryingInfo_")) {
        return currentNode.startPosition.top > 400;
      }

      if (currentNode.id?.startsWith("dryingImage")) {
        return currentNode.startPosition.top > 530;
      }

      return false;
    },
  });
}

export async function createPdfReportForProgramChains(
  vue: Vue,
  ...programChains: ProgramChain[]
) {
  const serialNumber = programChains[0]?.serialNumber;

  if (!serialNumber) {
    return null;
  }

  const pdfReportData = await getPdfReportDataDrying(
    serialNumber,
    programChains.map((chain) => chain.id)
  );

  if (!pdfReportData) {
    return null;
  }

  return new Promise<string>((resolve) => {
    const document = createPdfReportFromReportData(
      pdfReportData,
      ReportType.DRYING,
      vue,
      null
    );

    const reportDate = new Date();
    const timestamp = firestore.Timestamp.fromDate(reportDate);
    const deviceIdentifier = serialNumber;

    const fileName = `${vue.$t(
      "report.reportName.dryingReportFileNamePrefix"
    )}_${deviceIdentifier}_${formatDateCompact(timestamp)}.pdf`;

    const storageLocation = `reports/${getClientId()}/dryingReports/${fileName}`;

    document.getBlob(async (blob) => {
      const ref = storage.ref(storageLocation);
      await ref.put(blob);

      const downloadUrl = await ref.getDownloadURL();

      resolve(downloadUrl);
    });
  });
}

function getProjectName(projectDetails: ProjectDetails) {
  return isSimulatingErp() 
    ? projectDetails.clientData.projectNumberIres ?? projectDetails.clientData.projectNumber 
    : projectDetails.clientData.projectNumber ?? "";
}

export async function createPdfReportForProject(
  vue: Vue,
  projectDetails: ProjectDetails,
  reportType: ReportType,
  reportUsage: ReportUsage,
  timestamp: firestore.Timestamp,
  sendBy?: string
) {
  const pdfReportData = await getPdfReportDataForProject(
    projectDetails.id,
    reportType,
    reportUsage
  );
  
  const projectNumber = getProjectName(projectDetails);

  return new Promise<Report>((resolve) => {
    const document = createPdfReportFromReportData(
      pdfReportData,
      reportType,
      vue,
      projectNumber
    );

    const fileNamePostfix = vue
      .$t(
        `report.reportName.${
          reportType === ReportType.FINAL_REPORT
            ? "finalReportFileNamePrefix"
            : "interimReportFileNamePrefix"
        }`
      )
      .toString();

    const projectIdentifier =
      projectDetails.clientData.projectNumber !== ""
        ? getProjectName(projectDetails)
        : projectDetails.clientData.projectNumberIres ?? projectDetails.id;

    const formattedTimestamp = formatDateCompact(timestamp);
    const fileName = `${projectIdentifier}_${formattedTimestamp}_${fileNamePostfix}.pdf`;
    const folder = `${projectIdentifier}_${formatDateForReportsFolderName(timestamp)}`;

    const storageLocation = `reports/${getClientId()}/${folder}/${fileName}`;

    document.getBlob(async (blob) => {
      // Upload PDF
      await storage.ref(storageLocation).put(blob);

      const report: Report = {
        id: generateRandomUuid(),
        fileSizeInBytes: blob.size,
        storageLocation: `/${storageLocation}`,
        forwardStatus: UploadStatus.PENDING,
        projectId: projectDetails.id,
        createdBy: getActiveUserId(),
        projectName: getProjectName(projectDetails),
        projectNumber: getProjectName(projectDetails),
        reportDate: timestamp,
        startDate: projectDetails.clientData.projectStartDate,
        endDate: projectDetails.projectConclusionData?.projectConclusionDate,
        wasUploaded: true,
        sendBy: sendBy ?? null,
        lastTimeSend: sendBy ? timestamp : null,
        projectFinished: !!projectDetails.projectConclusionData
          ?.projectConclusionDate,
        outdated: false,
        reportType: reportType,
        usage: reportUsage,
      };

      await saveReport(report);

      resolve(report);
    });
  });
}

export async function createHyDryCertificateForProject(
  vue: Vue,
  projectDetails: ProjectDetails,
  timestamp: firestore.Timestamp,
  sendBy?: string
) {
  try {
    const projectIdentifier =
      projectDetails.clientData.projectNumber !== ""
        ? getProjectName(projectDetails)
        : projectDetails.clientData.projectNumberIres ?? projectDetails.id;

    const folderName = `${projectIdentifier}_${formatDateForReportsFolderName(timestamp)}`;

    const response = await createHyDryCertificate(
      projectDetails.id,
      folderName    
    );

    if (response.success) {
      const storageLocation = response.pdfRef;
      const fileSizeInBytes = response.fileSizeInBytes;

      const report: Report = filterOutUndefined({
        id: generateRandomUuid(),
        fileSizeInBytes: fileSizeInBytes ?? undefined,
        storageLocation: `/${storageLocation}`,
        forwardStatus: UploadStatus.PENDING,
        projectId: projectDetails.id,
        createdBy: getActiveUserId(),
        projectName: getProjectName(projectDetails),
        projectNumber: getProjectName(projectDetails),
        reportDate: timestamp,
        startDate: projectDetails.clientData.projectStartDate,
        endDate: projectDetails.projectConclusionData?.projectConclusionDate,
        wasUploaded: true,
        sendBy: sendBy ?? null,
        lastTimeSend: sendBy ? timestamp : null,
        projectFinished: !!projectDetails.projectConclusionData
          ?.projectConclusionDate,
        outdated: false,
        reportType: ReportType.FINAL_REPORT,
        extendedReportType: ExtendedReportType.FINAL_REPORT,
        usage: ReportUsage.EXTERNAL,
        extendedReportUsage: ExtendedReportUsage.HY_DRY_CERTIFICATE
      });

      await saveReport(report);

      return report;
    }

    return null;
  } catch (error) {
    return null;
  }
}
